import { useMutation } from '@apollo/client';
import { PROFESSIONAL_UPDATE_CLIENT_ALLERGIES, PROFESSIONAL_UPDATE_CLIENT_BIO, PROFESSIONAL_UPDATE_CLIENT_CONTACT_DETAILS, PROFESSIONAL_UPDATE_CLIENT_SPECIALISTS, PROFESSIONAL_UPDATE_CLIENT_VACCINATIONS } from '../../services/graphql/mutations/professional';
import { useCallback } from 'react';
import { CareerAndEducationDetailType, FamilyDetailType, PersonalContactDetailsType, SpecialistsType, VaccinationType } from '../../types';

type UpdateProfessionalClientSpecialists = {
  otherSpecialists: SpecialistsType[] & { _destroy?: boolean }
  _destroy?: boolean
} & SpecialistsType;

type UpdateProfessionalClientContactDetailsType = {
  personalContactDetailAttributes: PersonalContactDetailsType
  careerAndEducationDetailAttributes: CareerAndEducationDetailType
  familyDetailAttributes: FamilyDetailType
}

type UpdateProfessionalClientVaccinationsType = {
  optionVaccinationId: string
} & VaccinationType;

export const useUpdateProfessionalClientBio = () => {
  const [updateProfessionalClientBio, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_BIO);

  const updateData = useCallback(async (clientId: string, values: any) => {
    try {
      const { data } = await updateProfessionalClientBio({
        variables: {
          input: {
            clientId,
            params: {
              firstName: values.firstName,
              familyName: values.familyName,
              bioAttributes: {
                sex: values.sex,
                weight: values.weight,
                waistCircumference: values.waistCircumference,
                isVegetarian: values.isVegetarian,
                dateOfBirth: values.dateOfBirth,
                length: values.length,
              }
            },
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientBio: ', error);
    }
  }, [updateProfessionalClientBio]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientSpecialists = () => {
  const [updateProfessionalClientSpecialists, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_SPECIALISTS);

  const updateData = useCallback(async (clientId: string, values: UpdateProfessionalClientSpecialists) => {
    try {
      const { data } = await updateProfessionalClientSpecialists({
        variables: {
          input: {
            clientId,
            specialistsAttributes: {
              id: values?.id,
              name: values?.name,
              address: values?.address,
              telephoneCountryCode: values?.telephoneCountryCode,
              telephoneNumber: values?.telephoneNumber,
              forWhat: values?.forWhat,
              sinceWhen: values?.sinceWhen,
              _destroy: values?._destroy
            },
            otherHealthProfessionalsAttributes: values?.otherSpecialists
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientSpecialists: ', error);
    }
  }, [updateProfessionalClientSpecialists]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientContactDetails = () => {
  const [updateProfessionalClientContactDetails, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_CONTACT_DETAILS);

  const updateData = useCallback(async (clientId: string, values: UpdateProfessionalClientContactDetailsType) => {
    try {
      const { data } = await updateProfessionalClientContactDetails({
        variables: {
          input: {
            clientId,
            params: {
              familyDetailAttributes: values.familyDetailAttributes,
              careerAndEducationDetailAttributes: values.careerAndEducationDetailAttributes,
              personalContactDetailAttributes: values.personalContactDetailAttributes
            }
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientContactDetails: ', error);
    }
  }, [updateProfessionalClientContactDetails]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientVaccinations = () => {
  const [updateProfessionalClientVaccinations, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_VACCINATIONS);

  const updateData = useCallback(async (clientId: string, values: any) => {
    try {
      const { data } = await updateProfessionalClientVaccinations({
        variables: {
          input: {
            clientId,
            params: {
              id: values?.id,
              optionVaccinationId: values?.optionVaccinationId,
              otherName: values?.otherName,
              when: values?.when,
              physiologicalResponse: values?.physiologicalResponse
            }
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientVaccinations: ', error);
    }
  }, [updateProfessionalClientVaccinations]);

  return {
    update: updateData,
    loading
  };
};

export const useUpdateProfessionalClientAllergies = () => {
  const [updateProfessionalClientAllergies, { loading }] = useMutation(PROFESSIONAL_UPDATE_CLIENT_ALLERGIES);

  const updateData = useCallback(async (clientId: string, values: any) => {
    try {
      const { data } = await updateProfessionalClientAllergies({
        variables: {
          input: {
            clientId,
            params: {
              ...values
            }
          },
        }
      });
      return data;
    } catch (error) {
      console.log('Error in updateProfessionalClientAllergies: ', error);
    }
  }, [updateProfessionalClientAllergies]);

  return {
    update: updateData,
    loading
  };
};